import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Layout } from '@/components/Layout';
import { toast } from 'sonner';
import { axiosInstance } from '@/App';
import { Mail, CheckCircle2, XCircle, Send, Clock, AlertCircle } from 'lucide-react';

export default function EmailConfig({ user }) {
  const [config, setConfig] = useState({
    smtp_server: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    use_tls: true,
    from_email: '',
    from_name: 'C10 Platform',
    is_active: false
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    fetchConfig();
    fetchHistory();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axiosInstance.get('/email/config');
      setConfig(response.data);
    } catch (error) {
      toast.error('Error al cargar configuración');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axiosInstance.get('/email/history?limit=50');
      setHistory(response.data.history || []);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  const handleSave = async () => {
    if (!config.smtp_server || !config.smtp_user || !config.from_email) {
      toast.error('Por favor completa los campos obligatorios');
      return;
    }

    setSaving(true);
    try {
      await axiosInstance.post('/email/config', config);
      toast.success('Configuración guardada correctamente');
      fetchConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!config.smtp_server || !config.smtp_user) {
      toast.error('Completa servidor y usuario primero');
      return;
    }

    setTesting(true);
    try {
      const response = await axiosInstance.post('/email/test-connection');
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Error al probar conexión: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTesting(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) {
      toast.error('Introduce un email destino');
      return;
    }

    setSendingTest(true);
    try {
      const response = await axiosInstance.post('/email/send-test', {
        to_email: testEmail
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setTestEmail('');
        fetchHistory();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <p>Cargando...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="container mx-auto p-6 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-[#212B35]">Configuración de Email</h1>
          <p className="text-gray-600">Por defecto enviamos los emails desde el servicio centralizado de OSARED. Puedes (opcionalmente) configurar tu propio SMTP para usar tu dominio.</p>
        </div>

        {/* Hybrid model explainer */}
        <Alert className="border-blue-500 bg-blue-50">
          <AlertDescription className="text-blue-900">
            <div className="flex items-start gap-2">
              <Mail className="w-5 h-5 mt-0.5 text-blue-600 shrink-0" />
              <div className="space-y-1">
                <p className="font-semibold">📨 Cómo funciona el envío de emails</p>
                <p className="text-sm">
                  <strong>Por defecto:</strong> usamos un servicio centralizado de OSARED (Resend) que envía los correos
                  desde nuestra dirección. Cero configuración por tu parte, alta deliverability.
                </p>
                <p className="text-sm">
                  <strong>Opcional:</strong> si quieres que el remitente sea de TU dominio (ej. <code>facturacion@tu-empresa.com</code>),
                  rellena los campos SMTP de abajo y actívalo. En cuanto guardes con SMTP propio configurado,
                  los emails se enviarán desde ahí.
                </p>
              </div>
            </div>
          </AlertDescription>
        </Alert>

        {/* Status Alert */}
        <Alert className={config.is_active ? 'border-green-500 bg-green-50' : 'border-orange-500 bg-orange-50'}>
          <AlertDescription className="flex items-center gap-2">
            {config.is_active ? (
              <>
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span className="text-green-800">SMTP propio ACTIVO — los emails saldrán de tu servidor configurado</span>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 text-blue-600" />
                <span className="text-blue-800">Usando servicio de OSARED por defecto. Configura SMTP abajo si quieres tu propio dominio.</span>
              </>
            )}
          </AlertDescription>
        </Alert>

        {/* Configuration Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Configuración SMTP
            </CardTitle>
            <CardDescription>
              Configura los datos de tu servidor SMTP para enviar correos
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between p-4 border rounded-lg bg-gray-50">
              <div>
                <Label className="text-base font-semibold">Activar Email</Label>
                <p className="text-sm text-gray-600">Habilitar envío de correos electrónicos</p>
              </div>
              <Button
                variant={config.is_active ? 'default' : 'outline'}
                onClick={() => setConfig({ ...config, is_active: !config.is_active })}
                className={config.is_active ? 'bg-green-600 hover:bg-green-700' : ''}
              >
                {config.is_active ? 'Activado' : 'Desactivado'}
              </Button>
            </div>

            {config.is_active && (
              <>
                {/* Servidor SMTP */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Datos del Servidor SMTP</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="smtp_server">Servidor SMTP <span className="text-red-500">*</span></Label>
                      <Input
                        id="smtp_server"
                        value={config.smtp_server}
                        onChange={(e) => setConfig({ ...config, smtp_server: e.target.value })}
                        placeholder="smtp.gmail.com"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Ejemplos: smtp.gmail.com, smtp.office365.com
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="smtp_port">Puerto SMTP</Label>
                      <Input
                        id="smtp_port"
                        type="number"
                        value={config.smtp_port}
                        onChange={(e) => setConfig({ ...config, smtp_port: parseInt(e.target.value) })}
                        placeholder="587"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        587 (TLS) o 465 (SSL)
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="smtp_user">Usuario SMTP <span className="text-red-500">*</span></Label>
                      <Input
                        id="smtp_user"
                        value={config.smtp_user}
                        onChange={(e) => setConfig({ ...config, smtp_user: e.target.value })}
                        placeholder="tu-email@ejemplo.com"
                      />
                    </div>

                    <div>
                      <Label htmlFor="smtp_password">Contraseña SMTP <span className="text-red-500">*</span></Label>
                      <Input
                        id="smtp_password"
                        type="password"
                        value={config.smtp_password}
                        onChange={(e) => setConfig({ ...config, smtp_password: e.target.value })}
                        placeholder="••••••••"
                      />
                    </div>

                    <div>
                      <Label htmlFor="use_tls">Seguridad</Label>
                      <Select 
                        value={config.use_tls.toString()} 
                        onValueChange={(value) => setConfig({...config, use_tls: value === 'true'})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="true">TLS (Puerto 587)</SelectItem>
                          <SelectItem value="false">SSL (Puerto 465)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Email Remitente */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Datos del Remitente</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="from_email">Email Remitente <span className="text-red-500">*</span></Label>
                      <Input
                        id="from_email"
                        type="email"
                        value={config.from_email}
                        onChange={(e) => setConfig({ ...config, from_email: e.target.value })}
                        placeholder="noreply@tuempresa.com"
                      />
                    </div>

                    <div>
                      <Label htmlFor="from_name">Nombre del Remitente</Label>
                      <Input
                        id="from_name"
                        value={config.from_name}
                        onChange={(e) => setConfig({ ...config, from_name: e.target.value })}
                        placeholder="C10 Platform"
                      />
                    </div>
                  </div>
                </div>

                {/* Test Connection */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">Probar Configuración</h4>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleTestConnection}
                      disabled={testing}
                      variant="outline"
                      className="border-blue-300"
                    >
                      {testing ? 'Probando...' : 'Probar Conexión'}
                    </Button>
                  </div>
                </div>

                {/* Send Test Email */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-900 mb-2">Enviar Email de Prueba</h4>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="email@ejemplo.com"
                      className="max-w-xs"
                    />
                    <Button
                      onClick={handleSendTest}
                      disabled={sendingTest}
                      className="bg-yellow-600 hover:bg-yellow-700"
                    >
                      <Send className="w-4 h-4 mr-1" />
                      {sendingTest ? 'Enviando...' : 'Enviar Prueba'}
                    </Button>
                  </div>
                </div>
              </>
            )}

            {/* Botones de acción */}
            <div className="flex gap-4 pt-4 border-t">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-[#9C0F17] hover:bg-[#7A0C12]"
              >
                {saving ? 'Guardando...' : 'Guardar Configuración'}
              </Button>
              
              <Button
                onClick={fetchConfig}
                variant="outline"
              >
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Email History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Historial de Emails
            </CardTitle>
            <CardDescription>Últimos 50 correos enviados</CardDescription>
          </CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No hay emails enviados aún</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-2 text-left">Fecha</th>
                      <th className="p-2 text-left">Destinatario</th>
                      <th className="p-2 text-left">Asunto</th>
                      <th className="p-2 text-left">Tipo</th>
                      <th className="p-2 text-left">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((log, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-2">
                          {log.sent_at ? new Date(log.sent_at).toLocaleString() : '-'}
                        </td>
                        <td className="p-2">{log.to_email}</td>
                        <td className="p-2">{log.subject}</td>
                        <td className="p-2">
                          <span className={`px-2 py-1 rounded text-xs ${
                            log.type === 'test' ? 'bg-yellow-100 text-yellow-800' :
                            log.type === 'invoice' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {log.type === 'test' ? 'Prueba' : 
                             log.type === 'invoice' ? 'Factura' :
                             log.type === 'quote' ? 'Presupuesto' : 'Otro'}
                          </span>
                        </td>
                        <td className="p-2">
                          {log.status === 'success' ? (
                            <span className="text-green-600 flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" />
                              Enviado
                            </span>
                          ) : (
                            <span className="text-red-600 flex items-center gap-1">
                              <XCircle className="w-3 h-3" />
                              Error
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
